/*!
 * (c) 2026 Ocean Essentials Limited (HK). All rights reserved.
 * Proprietary code. Licensed, not sold. See LICENSE.md and Services Agreement.
 */

/**
 * Mobile Menu V3 controller
 */
(function () {
  "use strict";

  const MOBILE_BP = 1000;
  let controller;

  function initMobileMenuV3() {
    if (controller) controller.abort();
    controller = new AbortController();
    const signal = controller.signal;

    const menu = document.getElementById("mobile-menu-v3");
    if (!menu) return;

    const backBtn = document.getElementById("mm-back-btn");
    const logo = document.getElementById("mm-logo");
    const title = document.getElementById("mm-title");
    const panelMain = document.getElementById("mm-panel-main");
    const panelSails = document.getElementById("mm-panel-sails");
    const tablist = menu.querySelector(".mobile-menu-v3__discipline-tabs");
    const allTabpanel = document.getElementById("mm-sails-all");

    let currentLevel = 1;
    let motion;
    let motionVersion = 0;
    let closing = false;
    let savedOverflow;
    let submenuTrigger;
    let mainPanelScrollTop = 0;
    const container = menu.querySelector(".mobile-menu-v3__container");

    function animateMenu(opening) {
      const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      const continuing = motion && motion.playState !== "finished";
      const style = getComputedStyle(container);
      const from = continuing ? { opacity: style.opacity, transform: style.transform } : {
        opacity: opening ? 0 : 1,
        transform: opening && !reduced ? "translateY(-2%)" : "translateY(0)"
      };
      motion?.cancel();
      motion = container.animate([from, {
        opacity: opening ? 1 : 0,
        transform: opening || reduced ? "translateY(0)" : "translateY(-2%)"
      }], { duration: reduced ? 0 : opening ? 320 : 240, easing: "cubic-bezier(0.22, 1, 0.36, 1)", fill: "both" });
      return motion.finished.catch(() => {});
    }

    function releaseMenu() {
      if (menu.open) return;
      motionVersion++;
      closing = false;
      motion?.cancel();
      if (savedOverflow !== undefined) {
        document.body.style.overflow = savedOverflow;
        savedOverflow = undefined;
      }
      setTriggersExpanded(false);
      mainPanelScrollTop = 0;
      resetToMainPanel(false);
    }

    function isMobileViewport() {
      return window.innerWidth < MOBILE_BP;
    }

    function setTriggersExpanded(expanded) {
      document.querySelectorAll("[data-mobile-menu-v3-trigger]").forEach((trigger) => {
        if (isMobileViewport()) {
          trigger.setAttribute("aria-expanded", expanded ? "true" : "false");
        }
      });
    }

    function openMenu() {
      if (menu.open && !closing) return;
      motionVersion++;
      closing = false;
      if (!menu.open) {
        resetToMainPanel(false);
        savedOverflow = document.body.style.overflow;
        menu.showModal();
      }
      menu.querySelector("[data-close-menu]")?.focus({ preventScroll: true });
      document.body.style.overflow = "hidden";
      setTriggersExpanded(true);
      const version = motionVersion;
      animateMenu(true).then(() => {
        if (version !== motionVersion || !menu.open || menu.contains(document.activeElement)) return;
        // A nested popover can restore focus after a closing menu is reopened.
        menu.querySelector("[data-close-menu]")?.focus({ preventScroll: true });
      });
    }

    function closeMenu() {
      if (!menu.open || closing) return;
      closing = true;
      menu.querySelectorAll("x-popover[open]").forEach(popover => popover.hide({ animate: false }));
      const version = ++motionVersion;
      setTriggersExpanded(false);
      animateMenu(false).then(() => {
        if (version !== motionVersion) return;
        menu.close();
        releaseMenu();
      });
    }

    function drillDown(submenuType, submenuLabel) {
      if (closing) return;
      submenuTrigger = document.activeElement;
      currentLevel = 2;
      if (panelMain) mainPanelScrollTop = panelMain.scrollTop || 0;
      if (panelSails) panelSails.scrollTop = 0;
      panelMain.inert = true;
      panelSails.inert = false;
      panelMain.classList.add("is-slide-out");
      panelSails.classList.add("is-slide-in");
      backBtn.disabled = false;
      backBtn.classList.add("is-visible");
      logo.inert = true;
      logo.classList.add("is-hidden");
      title.classList.add("is-visible");
      if (submenuLabel) title.textContent = submenuLabel;
      filterByDiscipline("all");
      backBtn.focus({ preventScroll: true });
    }

    function resetToMainPanel(restoreFocus = true) {
      currentLevel = 1;
      if (panelSails) panelSails.scrollTop = 0;
      if (panelMain) panelMain.scrollTop = mainPanelScrollTop;
      panelMain.inert = false;
      panelSails.inert = true;
      panelMain.classList.remove("is-slide-out");
      panelSails.classList.remove("is-slide-in");
      backBtn.disabled = true;
      backBtn.classList.remove("is-visible");
      logo.inert = false;
      logo.classList.remove("is-hidden");
      title.classList.remove("is-visible");
      if (restoreFocus && menu.open) submenuTrigger?.focus({ preventScroll: true });
    }

    function updateTabRovingFocus(activeTab) {
      const tabs = menu.querySelectorAll(".mobile-menu-v3__discipline-tab");
      tabs.forEach((tab) => {
        tab.setAttribute("tabindex", tab === activeTab ? "0" : "-1");
      });
    }

    function filterByDiscipline(discipline) {
      const tabs = menu.querySelectorAll(".mobile-menu-v3__discipline-tab");
      const sections = menu.querySelectorAll("[data-discipline-section]");
      const cards = menu.querySelectorAll(".mobile-menu-v3__product-card");
      let activeTab = null;

      tabs.forEach((tab) => {
        const isActive = tab.dataset.discipline === discipline;
        tab.classList.toggle("is-active", isActive);
        tab.setAttribute("aria-selected", isActive ? "true" : "false");
        if (isActive) activeTab = tab;
      });

      if (allTabpanel) {
        allTabpanel.hidden = discipline !== "all";
      }

      if (discipline === "all") {
        sections.forEach((section) => section.classList.remove("is-hidden"));
        cards.forEach((card) => card.classList.remove("is-hidden"));
      } else {
        sections.forEach((section) => {
          section.classList.toggle("is-hidden", section.dataset.disciplineSection !== discipline);
        });
        cards.forEach((card) => {
          card.classList.toggle("is-hidden", card.dataset.discipline !== discipline);
        });
      }

      if (activeTab) updateTabRovingFocus(activeTab);
    }

    menu.querySelectorAll("[data-close-menu]").forEach((el) => {
      el.addEventListener("click", closeMenu, { signal });
    });

    if (backBtn) {
      backBtn.addEventListener("click", resetToMainPanel, { signal });
    }

    menu.querySelectorAll("[data-drilldown]").forEach((btn) => {
      btn.addEventListener("click", () => {
        drillDown(btn.getAttribute("data-drilldown"), btn.getAttribute("data-drilldown-label"));
      }, { signal });
    });

    menu.addEventListener("close", releaseMenu, { signal });

    menu.addEventListener("click", (e) => {
      if (e.target === menu) closeMenu();
    }, { signal });

    menu.addEventListener("cancel", (e) => {
      e.preventDefault();
      const popover = menu.querySelector("x-popover[open]");
      if (popover) { popover.hide(); return; }
      if (currentLevel === 2 && !closing) {
        resetToMainPanel();
      } else {
        closeMenu();
      }
    }, { signal });

    menu.querySelectorAll(".mobile-menu-v3__discipline-tab").forEach((tab) => {
      tab.addEventListener("click", () => {
        filterByDiscipline(tab.dataset.discipline);
      }, { signal });
    });

    if (tablist) {
      tablist.addEventListener("keydown", (e) => {
        const tabs = Array.from(menu.querySelectorAll(".mobile-menu-v3__discipline-tab"));
        const currentIndex = tabs.indexOf(document.activeElement);
        if (currentIndex === -1) return;

        let nextIndex = null;
        if (e.key === "ArrowRight") nextIndex = (currentIndex + 1) % tabs.length;
        else if (e.key === "ArrowLeft") nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
        else if (e.key === "Home") nextIndex = 0;
        else if (e.key === "End") nextIndex = tabs.length - 1;
        else return;

        e.preventDefault();
        const nextTab = tabs[nextIndex];
        nextTab.focus();
        filterByDiscipline(nextTab.dataset.discipline);
      }, { signal });
    }

    window.openMobileMenuV3 = openMenu;
    document.addEventListener("mobile-menu-v3:open", openMenu, { signal });

    const cartCountEl = document.getElementById("mm-cart-count");
    document.addEventListener("cart:change", (e) => {
      if (cartCountEl) cartCountEl.textContent = e.detail?.cart?.item_count ?? 0;
    }, { signal });

    function updateTriggers() {
      const useMobileMenu = isMobileViewport();
      if (!useMobileMenu && menu.open) closeMenu();
      document.querySelectorAll("[data-mobile-menu-v3-trigger]").forEach((trigger) => {
        trigger.setAttribute("aria-controls", useMobileMenu ? "mobile-menu-v3" : "menu-drawer");
        trigger.setAttribute("aria-haspopup", useMobileMenu ? "dialog" : "true");
        if (!menu.open) {
          trigger.setAttribute("aria-expanded", "false");
        }
      });
    }

    resetToMainPanel(false);
    updateTriggers();

    document.querySelectorAll("[data-mobile-menu-v3-trigger]").forEach((trigger) => {
      trigger.addEventListener("click", (e) => {
        if (isMobileViewport()) {
          e.preventDefault();
          e.stopPropagation();

          const oldDrawer = document.getElementById("menu-drawer");
          if (oldDrawer && oldDrawer.open) {
            oldDrawer.open = false;
            oldDrawer.hide?.();
          }

          openMenu();
        }
      }, { capture: true, signal });
    });

    let resizeTimer;
    window.addEventListener("resize", () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(updateTriggers, 150);
    }, { signal });

    signal.addEventListener("abort", () => {
      clearTimeout(resizeTimer);
      motionVersion++;
      motion?.cancel();
      if (menu.open) menu.close();
      releaseMenu();
      if (window.openMobileMenuV3 === openMenu) delete window.openMobileMenuV3;
    });

    document.addEventListener("shopify:section:unload", (e) => {
      if (e.target.contains(menu)) {
        controller.abort();
      }
    }, { signal });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initMobileMenuV3);
  } else {
    initMobileMenuV3();
  }

  document.addEventListener("shopify:section:load", (e) => {
    if (e.target.querySelector("#mobile-menu-v3")) initMobileMenuV3();
  });
})();
